import { WorkTaskPreference } from "../types/ganttChart";

export const groupBy = <T, R extends string | number | symbol>(
  data: T[],
  grouper: (input: T) => R,
): Map<R, T[]> => {
  const groups = new Map<R, T[]>();

  for (let item of data) {
    const key = grouper(item);
    if (groups.has(key)) {
      groups.get(key)!.push(item);
      continue;
    }
    groups.set(key, [item]);
  }

  return groups;
};

export const sortByName = <T>(
  arr: T[],
  getName: (item: T) => string | null | undefined,
): T[] =>
  [...arr].sort((a, b) =>
    (getName(a) ?? "").localeCompare(getName(b) ?? "", "da"),
  );

export const sortBykpi = (preferences: WorkTaskPreference[]): WorkTaskPreference[] =>
  [...preferences].sort((a, b) => {
    if (a.hasCompetence !== b.hasCompetence) return a.hasCompetence ? -1 : 1;
    return b.score - a.score;
  });

export const sortByStartTime = <T extends { start: string }>(arr: T[]): T[] =>
  [...arr].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

export const filterBySearchTerm = <T>(
  arr: T[],
  getFields: (item: T) => string | null | undefined | (string | null | undefined)[],
  searchTerm: string,
): T[] => {
  if (!searchTerm.trim()) return arr;
  const lower = searchTerm.toLowerCase();
  return arr.filter((item) => {
    const fields = getFields(item);
    const values = Array.isArray(fields) ? fields : [fields];
    return values.some((value) => (value ?? "").toLowerCase().includes(lower));
  });
};

// Missing start = constraint has always been active; missing end = never expires; missing both = always active.
export const isConstraintActive = (start?: string, end?: string): boolean => {
  const now = new Date();
  if (!start && !end) return true;
  if (!start) return now <= new Date(end!);
  if (!end) return now >= new Date(start);
  return new Date(start) <= now && now <= new Date(end);
};

export const intersect = <T extends string | number>(
  arr1: T[],
  arr2: T[],
): T[] => {
  const obj = {} as { [key in T]: boolean };
  for (const item of arr2) {
    obj[item] = true;
  }
  return arr1.filter((x) => obj[x]);
};
